import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Settings,
  Info,
  Lock,
  Cloud,
  FileText,
  Eye,
  Pencil,
  UserCog,
  ShieldCheck,
  CheckCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

const DocControlSettings: React.FC = () => {
  const { t } = useTranslation()

  const roles = [
    { key: 'viewer', icon: Eye, color: 'text-muted-foreground' },
    { key: 'editor', icon: Pencil, color: 'text-blue-500' },
    { key: 'manager', icon: UserCog, color: 'text-green-600' },
    { key: 'admin', icon: ShieldCheck, color: 'text-red-500' },
  ]

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Main content */}
      <div className="lg:col-span-2 space-y-4">
        {/* General Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings className="h-4 w-4 text-muted-foreground" />
              {t('DocControl_general_settings')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
              <Info className="h-4 w-4 shrink-0" />
              {t('DocControl_settings_coming_soon')}
            </div>
            <p className="text-sm text-muted-foreground">
              {t('DocControl_settings_description')}
            </p>
          </CardContent>
        </Card>

        {/* Permission Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock className="h-4 w-4 text-muted-foreground" />
              {t('DocControl_permission_settings')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">{t('DocControl_permission_roles')}</h4>
            <div className="space-y-2">
              {roles.map((role) => {
                const Icon = role.icon
                return (
                  <div
                    key={role.key}
                    className="flex items-start gap-3 rounded-md bg-muted/50 p-3"
                  >
                    <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', role.color)} />
                    <div className="text-sm">
                      <span className="font-medium">{t(`DocControl_role_${role.key}`)}:</span>{' '}
                      <span className="text-muted-foreground">{t(`DocControl_role_${role.key}_desc`)}</span>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="flex items-start gap-2 rounded-md border p-3 text-sm">
              <Info className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>{t('DocControl_permission_inheritance_info')}</span>
            </div>
          </CardContent>
        </Card>

        {/* Cloud Integration */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Cloud className="h-4 w-4 text-muted-foreground" />
              {t('DocControl_cloud_integration')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <Info className="h-4 w-4 shrink-0" />
              {t('DocControl_cloud_integration_coming_soon')}
            </div>

            <p className="text-sm text-muted-foreground">{t('DocControl_cloud_integration_desc')}</p>

            <div className="flex gap-3">
              {[
                { name: 'OneDrive', color: 'bg-[#0078d4]' },
                { name: 'Google Drive', color: 'bg-[#4285f4]' },
                { name: 'Dropbox', color: 'bg-[#0061ff]' },
              ].map((provider) => (
                <div
                  key={provider.name}
                  className="flex flex-col items-center gap-2 rounded-md border p-3 w-[100px]"
                >
                  <div className={cn('h-9 w-9 rounded-full flex items-center justify-center opacity-50', provider.color)}>
                    <Cloud className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-xs text-muted-foreground">{provider.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sidebar */}
      <div className="space-y-4">
        {/* About */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              {t('DocControl_about')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{t('DocControl_about_description')}</p>

            <div>
              <h4 className="text-sm font-medium mb-2">{t('DocControl_features')}:</h4>
              <div className="space-y-1.5">
                {[
                  t('DocControl_feature_folders'),
                  t('DocControl_feature_versions'),
                  t('DocControl_feature_permissions'),
                  t('DocControl_feature_sharing'),
                  t('DocControl_feature_transmittals'),
                  t('DocControl_feature_activity'),
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-3.5 w-3.5 text-primary shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Storage Info */}
        <Card>
          <CardContent className="pt-6 space-y-3">
            <h4 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Cloud className="h-4 w-4" />
              {t('DocControl_storage')}
            </h4>

            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: '0%' }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {t('DocControl_storage_used', { used: '0 MB', total: 'Unlimited' })}
              </span>
              <Badge variant="success" className="text-[10px]">
                Unlimited
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default DocControlSettings
